import React, { FC, memo, useEffect } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLoggedUserData, useGetUserByIdQuery } from 'entities/User';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { AccountDetailsForm } from 'widgets/AccountDetailsForm';
import { CustomerDetailsForm } from 'widgets/CustomerDetailsForm';
import { Button } from 'shared/ui/Button';
import { requestFormModalActions, RequestModalMode } from 'widgets/RequestModal';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';

export const MobileMyAccountPage: FC = memo(() => {
  const { t } = useAppTranslation(['profile', 'booking', 'common']);

  const { userId } = useParams();
  const { data } = useGetUserByIdQuery({ userId, params: { warehouses: true, userDebt: true } });
  const { isDesktop } = useScreenBreakpoints();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loggedUser = useAppSelector(getLoggedUserData);

  useEffect(() => {
    if (isDesktop) {
      navigate(`${AppRoutes.PROFILE}/${userId}`);
    }
  }, [isDesktop, navigate, userId]);

  if (!userId) {
    return <div>{t('User not exists')}</div>;
  }

  if (!data) {
    return <div>{t('User not authorized')}</div>;
  }

  const openRequestModal = (): void => {
    dispatch(
      requestFormModalActions.openRequestModal({
        title: t('Let`s get to know each other better!', { ns: 'booking' }),
        description: t('Please provide us with your information so that we can suggest the best pricing options that meet your needs.', {
          ns: 'booking',
        }),
        mode: RequestModalMode.CONTACT_US,
      }),
    );
  };

  const {
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    country,
    city,
    street,
    zip,
    house,
    apartment,
    language,
    identificationNumber,
    additionalEmail,
    company,
    accountType,
    bankingRequisites,
    role,
    additionalPhone,
    profileVerificationStatus,
  } = data;

  return (
    <div className="px-4">
      <div className="flex flex-col rounded-lg gap-2 pb-6">
        <AccountDetailsForm
          userId={userId}
          email={email}
          phone={phone}
          firstName={firstName}
          lastName={lastName}
          accountType={accountType}
          company={company}
          identificationNumber={identificationNumber}
          loggedUserId={loggedUser?.userId}
          profileVerificationStatus={profileVerificationStatus}
        />
        <CustomerDetailsForm
          userId={userId}
          dateOfBirth={dateOfBirth}
          email={email}
          additionalEmail={additionalEmail}
          language={language}
          bankingRequisites={bankingRequisites}
          phone={phone}
          additionalPhone={additionalPhone}
          country={country}
          city={city}
          street={street}
          zip={zip}
          house={house}
          apartment={apartment}
          firstName={firstName}
          lastName={lastName}
          accountType={accountType}
          company={company}
          role={role}
          identificationNumber={identificationNumber}
        />
      </div>
      <div className="fixed bottom-0 left-0 w-full z-20 bg-backgroundColor">
        <div className="flex justify-center p-2 px-4">
          <Button onClick={openRequestModal} containerClassName="w-full" size="medium">
            {t('Contact us', { ns: 'common' })}
          </Button>
        </div>
      </div>
    </div>
  );
});
